import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { roleService } from '@/services/roleService'
import { ChevronDown, Check, Plus, Edit3, Trash2, X, Loader2 } from 'lucide-react'

export function RoleSelect({ value, onChange }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [adding, setAdding] = useState(false)
  const [editingRoleId, setEditingRoleId] = useState(null)
  const [editName, setEditName] = useState('')
  const [updating, setUpdating] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const { data, isLoading } = useQuery('roles', roleService.getAllRoles, {
    onError: (err) => toast.error('Failed to fetch roles: ' + (err.response?.data?.error || err.message))
  })

  const roles = data?.data?.roles || []
  const selectedRole = roles.find((role) => role.id === value)

  function cancelEdit(event) {
    event?.stopPropagation()
    setEditingRoleId(null)
    setEditName('')
  }

  async function handleAdd(event) {
    event.preventDefault()
    event.stopPropagation()
    const trimmed = newRoleName.trim()
    if (!trimmed) return

    setAdding(true)
    try {
      const response = await roleService.createRole({ name: trimmed })
      toast.success('Role added')
      setNewRoleName('')
      setAddOpen(false)
      queryClient.invalidateQueries('roles')
      const createdId = response?.data?.role?.id
      if (createdId) onChange(createdId)
    } catch (err) {
      toast.error('Failed to add role: ' + (err.response?.data?.error || err.message))
    } finally {
      setAdding(false)
    }
  }

  function startEdit(event, role) {
    event.stopPropagation()
    setEditingRoleId(role.id)
    setEditName(role.name)
  }

  async function handleUpdate(event, id) {
    event.stopPropagation()
    const trimmed = editName.trim()
    if (!trimmed) return

    setUpdating(true)
    try {
      await roleService.updateRole(id, { name: trimmed })
      toast.success('Role updated')
      setEditingRoleId(null)
      setEditName('')
      queryClient.invalidateQueries('roles')
    } catch (err) {
      toast.error('Failed to update role: ' + (err.response?.data?.error || err.message))
    } finally {
      setUpdating(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await roleService.deleteRole(deleteTarget.id)
      toast.success('Role deleted')
      queryClient.invalidateQueries('roles')
      if (value === deleteTarget.id) onChange(null)
      setDeleteTarget(null)
    } catch (err) {
      toast.error('Failed to delete role: ' + (err.response?.data?.error || err.message))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) cancelEdit()
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 flex-1 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <span className={cn(!selectedRole && 'text-muted-foreground')}>
              {selectedRole ? selectedRole.name : 'Select a role'}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[220px] p-2" align="start">
          <div className="max-h-64 space-y-0.5 overflow-y-auto">
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                value == null && 'font-medium'
              )}
              onClick={() => { onChange(null); setOpen(false) }}
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                {value == null && <Check className="h-4 w-4" />}
              </span>
              No role
            </button>

            {isLoading ? (
              <p className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading roles...
              </p>
            ) : roles.length === 0 ? (
              <p className="px-2 py-3 text-sm text-muted-foreground">No roles yet. Add one using the + button.</p>
            ) : (
              roles.map((role) => {
                const isEditing = editingRoleId === role.id
                return (
                  <div key={role.id} className="group flex items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-accent">
                    {isEditing ? (
                      <>
                        <Input
                          value={editName}
                          onChange={(event) => setEditName(event.target.value)}
                          className="h-7 flex-1"
                          autoFocus
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') handleUpdate(event, role.id)
                            if (event.key === 'Escape') cancelEdit(event)
                          }}
                        />
                        <Button type="button" size="sm" className="h-7 w-7 shrink-0 p-0" disabled={updating || !editName.trim()} onClick={(event) => handleUpdate(event, role.id)}>
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="h-7 w-7 shrink-0 p-0" onClick={cancelEdit}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={cn('flex flex-1 items-center gap-2 rounded-sm px-1 py-1.5 text-left text-sm', value === role.id && 'font-medium')}
                          onClick={() => { onChange(role.id); setOpen(false) }}
                        >
                          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                            {value === role.id && <Check className="h-4 w-4" />}
                          </span>
                          <span className="truncate">{role.name}</span>
                        </button>
                        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 shrink-0 p-0 opacity-0 group-hover:opacity-100" onClick={(event) => startEdit(event, role)}>
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 shrink-0 p-0 text-destructive opacity-0 group-hover:opacity-100"
                          onClick={(event) => { event.stopPropagation(); setDeleteTarget(role); setOpen(false) }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={addOpen} onOpenChange={(next) => { setAddOpen(next); if (!next) setNewRoleName('') }}>
        <DialogTrigger asChild>
          <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" title="Add role">
            <Plus className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add role</DialogTitle>
          </DialogHeader>
          <form id="add-role-form" onSubmit={handleAdd}>
            <Input
              value={newRoleName}
              onChange={(event) => setNewRoleName(event.target.value)}
              placeholder="Role name"
              autoFocus
            />
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button type="submit" form="add-role-form" disabled={adding || !newRoleName.trim()}>
              {adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(next) => { if (!next) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this role?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently deleted. Authors currently assigned this role will show as having no role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(event) => { event.preventDefault(); handleDelete() }}
            >
              {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
